import React, { useCallback, useEffect, useState } from "react";
import { Container, Row, Col, Form, Button } from "react-bootstrap";
import { processRefreshArabicInputLemmas, processRefreshRomanizationInputLemmas } from "../common/text";
import LoadingDataSkelton from "./skeltons/loading-data-skelton";
import TranslatingResults from "./translating-result";
import { TranslateMethod } from "../common/models";
import { defaultFontSize } from "../common/style-consts";
import { Link } from "@mui/material";
import { AutoComplete } from "primereact/autocomplete";
import ReportIssueModal from "./report-issue-modal";
import { useSearchParams } from "react-router-dom";
import useSearchQuerySuggestions from "../hooks/useSearchQuerySuggestions";
import useTranslator from "../hooks/useTranslator";

const MainPage = () => {
  // Define state for report issue modal
  const [openReportIssueModal, setReportIssueModal] = useState(false);

  // use search params state from react router and define state for it
  const newQueryParameters: URLSearchParams = new URLSearchParams();
  const [currentQueryParameters, setSearchParams] = useSearchParams();

  // States for input values
  const [arabicSearchQuery, setArabicSearchQuery] = useState(
    currentQueryParameters.get("type") == "arabic" ? currentQueryParameters.get("q") ?? "" : ""
  );
  const [romanizationSearchQuery, setRomanizationSearchQuery] = useState(
    currentQueryParameters.get("type") == "umschrift"
      ? processRefreshRomanizationInputLemmas(currentQueryParameters.get("q") ?? "")
      : ""
  );
  const [translationType, setTranslationType] = useState<TranslateMethod | undefined>(
    currentQueryParameters.get("type")
      ? currentQueryParameters.get("type") == "arabic"
        ? TranslateMethod.Arabic
        : TranslateMethod.Umschrift
      : undefined
  );

  // Define state to handle close of report issue modal (ignore closing on press outside)
  const handleCloseReportIssueModal = useCallback((e?: any, reason?: "backdropClick" | "escapeKeyDown") => {
    if (reason === "backdropClick") {
      return;
    }
    setReportIssueModal(false);
  }, []);

  // Define function to handle search query changing for neither arabic or romanization input
  const handleSearchQueryChange = (searchQuery: string, type: TranslateMethod) => {
    newQueryParameters.set("type", type);
    newQueryParameters.set("q", searchQuery);
    setSearchParams(newQueryParameters);
    setTranslationType(type);
    if (searchQuery == "") {
      setTranslationType(undefined);
      setSearchParams(new URLSearchParams());
    }
    if (type == TranslateMethod.Arabic) {
      setRomanizationSearchQuery("");
      const newValue = processRefreshArabicInputLemmas(searchQuery);
      setArabicSearchQuery(newValue);
    } else if (type == TranslateMethod.Umschrift) {
      setArabicSearchQuery("");
      const newValue = processRefreshRomanizationInputLemmas(searchQuery);
      setRomanizationSearchQuery(newValue);
    }
  };

  // Use translation and getSuggestied
  const { arabicSuggestions, romanizationSuggestions, fetchSuggestions, isLoadingSuggestions } =
    useSearchQuerySuggestions({ type: translationType });
  const { translate, translationResults, isTranslating } = useTranslator({
    type: translationType,
    arabicSearchQuery,
    romanizationSearchQuery,
  });

  // Define function to handle keydown event from neither arabic or romanization input
  const handleSearchFieldKeyDown = (e: any) => {
    if (e.key == "Enter") {
      translate();
    }
  };

  useEffect(() => {
    if (currentQueryParameters.get("type") != null && currentQueryParameters.get("q") != null) {
      translate();
    }
  }, []);

  // render the component
  return (
    <>
      <Container fluid style={{ height: "100%", display: "flex", flexDirection: "column" }}>
        {/* Header */}
        <Row className="text-white text-center py-4 bg-dark">
          <Row>
            <Col>
              <h1>Lugha Lingua</h1>
            </Col>
          </Row>

          {/* Content */}
          <Row className="justify-content-center align-items-center g-2">
            <Col xs={12} md={4}>
              {/* Romanization Input */}
              <Form.Group controlId="romanization-input">
                <AutoComplete
                  style={{ width: "100%" }}
                  placeholder="Umschrift"
                  suggestions={romanizationSuggestions}
                  completeMethod={(e: any) => fetchSuggestions(e.query)}
                  value={romanizationSearchQuery}
                  onChange={(e: any) => handleSearchQueryChange(e.value, TranslateMethod.Umschrift)}
                  onKeyDown={handleSearchFieldKeyDown}
                />
              </Form.Group>
            </Col>

            <Col xs={12} md={4}>
              {/* Arabic Input */}
              <Form.Group controlId="arabic-input">
                <AutoComplete
                  placeholder="Arabisch"
                  style={{ width: "100%" }}
                  suggestions={arabicSuggestions}
                  completeMethod={(e: any) => fetchSuggestions(e.query)}
                  value={arabicSearchQuery}
                  onChange={(e: any) => handleSearchQueryChange(e.value, TranslateMethod.Arabic)}
                  onKeyDown={handleSearchFieldKeyDown}
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={4} style={{ width: "auto" }}>
              <Button
                disabled={isTranslating || !translationType}
                onClick={() => translate()}
                style={{ fontSize: defaultFontSize }}
              >
                {isTranslating ? "Suchen..." : "Suche"}
              </Button>
            </Col>
          </Row>
        </Row>

        {/* Result */}
        <Row className="bg-light" style={{ flex: 12 }}>
          {/* This row will take the remaining height */}
          {/* TODO : View Results Here! */}
          <div className="d-flex justify-content-center align-items-center">
            {isTranslating ? <LoadingDataSkelton /> : <TranslatingResults data={translationResults} />}
          </div>
        </Row>
        {/* Report Issue */}
        <Row className="bg-light" style={{ flex: 1, justifyContent: "right" }}>
          <Link sx={{ cursor: "pointer", width: "fitContent" }} onClick={() => setReportIssueModal(true)}>
            Problem melden
          </Link>
        </Row>
        <ReportIssueModal open={openReportIssueModal} handleClose={handleCloseReportIssueModal} />
      </Container>
    </>
  );
};

export default MainPage;
